/* RESPONSIBLE TEAM: team-purchase */

import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import { inject as service } from '@ember/service';
import { BILLING_PERIODS, PRICING_5_X_EARLY_STAGE_FREE_SOLUTION_ID } from 'embercom/lib/billing';
import type Session from 'embercom/services/session';
import moment from 'moment-timezone';

export enum CheckoutType {
  EarlyStagePostPurchase = 'early_stage_post_purchase',
  EarlyStageFreePostPurchase = 'early_stage_free_post_purchase',
  ACHPostPurchase = 'ach_post_purchase',
  ACHPostAnnualMigration = 'ach_post_annual_migration',
  DirectCheckoutPostPurchase = 'direct_checkout_post_purchase',
  DirectCheckoutPostAnnualMigration = 'direct_checkout_post_annual_migration',
  OneDollarTrialPostStart = 'one_dollar_trial_post_start',
}

export const DEFAULT_CHECKOUT_TYPE = CheckoutType.DirectCheckoutPostAnnualMigration;

export default class Success extends Route {
  @service declare appService: $TSFixMe;
  @service declare router: RouterService;
  @service declare customerService: $TSFixMe;
  @service declare session: Session;
  @service declare modelDataCacheService: $TSFixMe;

  isBillingPeriodMigration = false;

  queryParams = {
    isBillingPeriodMigration: {
      refreshModel: false,
      replace: true,
    },
  };

  async beforeModel(transition?: Transition | undefined) {
    await this.reloadApp();
    await this.reloadCustomer();
    await this.reloadWorkspaceAttributes();

    if (!this.checkoutType || !this.billingPeriod || !this.firstInvoicedOnDate) {
      await this.session.workspace.fetchRequiredAttributes();
    }
    if (!this.checkoutType || !this.billingPeriod || !this.firstInvoicedOnDate) {
      this.router.transitionTo('apps.app.index');
    }
    this.isBillingPeriodMigration =
      transition?.to?.queryParams?.isBillingPeriodMigration === 'true';
  }

  private async reloadApp() {
    await this.appService.app.reload();
    await this.appService.app.updateLocalCache(this.modelDataCacheService); // Issue with stale cached hasActiveSubscription state after conversion on certain pages - addressed by updating app model local cache
  }

  private async reloadCustomer() {
    // Need fresh customer state to determine success page content
    await this.customerService.resetAll();
    await this.customerService.refreshCustomer();
  }

  private async reloadWorkspaceAttributes() {
    // Continue button action can transition to inbox - workspace attributes need to be refetched so that user doesn't get redirected to expired subscription page
    if (this.session.isWorkspaceLoaded) {
      await this.session.workspace.fetchRequiredAttributes({ forceReload: true });
    }
  }

  async model() {
    return {
      checkoutType: this.checkoutType,
      planId: this.planId,
      firstInvoicedOnDate: this.firstInvoicedOnDate,
      billingPeriod: this.billingPeriod,
    };
  }

  get checkoutType(): CheckoutType {
    return Object.values(CheckoutType).find(this.isCheckoutType, this) ?? DEFAULT_CHECKOUT_TYPE;
  }

  get planId() {
    let { billableCustomerPlans, activePlanTrials } = this.customerService;
    let billablePlanId = billableCustomerPlans?.find((plan: any) => !plan.addon)?.idAsNumber;
    let trialPlanId = activePlanTrials?.find((plan: any) => !plan.addon)?.idAsNumber;
    let planIdAsNumber = billablePlanId ?? trialPlanId;
    return planIdAsNumber ? String(planIdAsNumber) : undefined;
  }

  get firstInvoicedOnDate() {
    let invoiceDate = moment()
      .set('date', this.customerService.customer.subscription.invoiceDayOfMonth) // invoiceDayOfMonth and moment date are both 1-indexed
      .set('month', this.customerService.customer.subscription.initialInvoiceMonthOfYear - 1); // initialInvoiceMonthOfYear is 1-indexed, moment month is 0-indexed

    // if the future invoice date falls into year update the year value.
    if (invoiceDate < moment()) {
      invoiceDate.add(1, 'year');
    }
    return invoiceDate;
  }

  get billingPeriod() {
    if (this.customerService.customer.subscription.isBilledAnnually) {
      return BILLING_PERIODS.Annual;
    }
    return BILLING_PERIODS.Monthly;
  }

  private get currentlyOnEarlyStage() {
    return !!this.customerService.customer.currentlyOnEarlyStage;
  }

  private get hasEarlyStageFreeSolutionId() {
    return (
      String(this.customerService.customer.earlyStageApplicationSolutionId) ===
      PRICING_5_X_EARLY_STAGE_FREE_SOLUTION_ID
    );
  }

  private isCheckoutType(checkoutType: CheckoutType) {
    return this.checkoutTypeCriteria[checkoutType];
  }

  private get checkoutTypeCriteria(): Record<CheckoutType, boolean> {
    return {
      [CheckoutType.EarlyStagePostPurchase]: this.isEarlyStagePostPurchase,
      [CheckoutType.EarlyStageFreePostPurchase]: this.isEarlyStageFreePostPurchase,
      [CheckoutType.ACHPostPurchase]: this.isACHPostPurchase,
      [CheckoutType.ACHPostAnnualMigration]: this.isACHPostAnnualMigration,
      [CheckoutType.DirectCheckoutPostPurchase]: this.isDirectCheckoutPostPurchase,
      [CheckoutType.DirectCheckoutPostAnnualMigration]: this.isDirectCheckoutPostAnnualMigration,
      [CheckoutType.OneDollarTrialPostStart]: this.isOneDollarTrialPostStart,
    };
  }

  private get isEarlyStagePostPurchase() {
    return this.currentlyOnEarlyStage && !this.hasEarlyStageFreeSolutionId;
  }

  private get isEarlyStageFreePostPurchase() {
    return this.currentlyOnEarlyStage && this.hasEarlyStageFreeSolutionId;
  }

  private get isACHPostPurchase() {
    return this.isACH && !this.isBillingPeriodMigration;
  }

  private get isACHPostAnnualMigration() {
    return this.isACH && this.isBillingPeriodMigration;
  }

  private get isACH() {
    return false;
  }

  private get isDirectCheckoutPostAnnualMigration() {
    return this.isDirectCheckout && this.isBillingPeriodMigration;
  }

  private get isDirectCheckoutPostPurchase() {
    return this.isDirectCheckout && !this.isBillingPeriodMigration;
  }

  private get isDirectCheckout() {
    return !this.isEarlyStagePostPurchase && !this.isOneDollarTrialPostStart && !this.isACH;
  }

  private get isOneDollarTrialPostStart() {
    return !!this.customerService.customer.inTrialExtension;
  }
}
